/* NUOMA 3.6 — Favoris sans compte client.
   Stockage local du navigateur (conservé après fermeture), synchronisé entre les onglets
   et entre les pages. Les données affichées sur la page « Mes favoris » sont relues
   auprès de Shopify (prix et disponibilité à jour). */
(() => {
  'use strict';
  const KEY = 'nuoma:favorites:v1';
  const root = () => window.Shopify?.routes?.root || '/';
  const NUOMA = (window.NUOMA = window.NUOMA || {});

  function read() {
    try { const list = JSON.parse(localStorage.getItem(KEY) || '[]'); return Array.isArray(list) ? list.filter(f => f && f.handle) : []; }
    catch (_) { return []; }
  }
  function write(list) {
    try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (_) { /* navigation privée : favoris le temps de la session */ }
    memory = list;
    sync();
    document.dispatchEvent(new CustomEvent('nuoma:favorites:change', {detail: {list}}));
  }
  let memory = read();
  const has = handle => memory.some(f => f.handle === handle);

  function sync(scope = document) {
    const handles = new Set(memory.map(f => f.handle));
    scope.querySelectorAll('[data-fav-toggle]').forEach(button => {
      const on = handles.has(button.dataset.handle);
      if (button.getAttribute('aria-pressed') === String(on)) return; // rien à changer
      button.setAttribute('aria-pressed', String(on));
      button.classList.toggle('is-active', on);
      const title = button.dataset.title || 'ce produit';
      button.setAttribute('aria-label', on ? `Retirer ${title} des favoris` : `Ajouter ${title} aux favoris`);
    });
    document.querySelectorAll('[data-fav-count]').forEach(count => {
      if (count.textContent !== String(memory.length)) count.textContent = memory.length;
      if (count.hidden !== (memory.length === 0)) count.hidden = memory.length === 0;
    });
  }

  function toggle(button) {
    const handle = button.dataset.handle;
    if (!handle) return;
    if (has(handle)) {
      write(memory.filter(f => f.handle !== handle));
      NUOMA.toast?.('Retiré de vos favoris');
    } else {
      write([{handle, title: button.dataset.title || '', url: button.dataset.url || root() + 'products/' + handle, image: button.dataset.image || '', price: Number(button.dataset.price) || 0, savedAt: Date.now()}, ...memory.filter(f => f.handle !== handle)]);
      button.classList.remove('is-pulsing');
      void button.offsetWidth;
      button.classList.add('is-pulsing');
      document.querySelectorAll('.nuoma-fav-link').forEach(link => { link.classList.remove('is-bumped'); void link.offsetWidth; link.classList.add('is-bumped'); });
      NUOMA.toast?.('Ajouté à vos favoris');
    }
  }

  document.addEventListener('click', event => {
    const button = event.target.closest('[data-fav-toggle]');
    if (!button) return;
    event.preventDefault();
    event.stopPropagation();
    toggle(button);
  });
  // Autre onglet ou fenêtre : mise à jour immédiate.
  addEventListener('storage', event => { if (event.key === KEY) { memory = read(); sync(); renderPage(); } });
  // Retour arrière (cache de navigation) : relire l'état.
  addEventListener('pageshow', () => { memory = read(); sync(); });
  // Cartes ajoutées plus tard (filtres, recommandations, carrousel) : état du cœur appliqué.
  let pending = 0;
  new MutationObserver(mutations => {
    if (pending || !mutations.some(m => [...m.addedNodes].some(n => n.nodeType === 1 && (n.matches?.('[data-fav-toggle]') || n.querySelector?.('[data-fav-toggle]'))))) return;
    pending = requestAnimationFrame(() => { pending = 0; sync(); });
  }).observe(document.documentElement, {childList: true, subtree: true});

  /* ---------------- Page « Mes favoris » ----------------
     Chaque favori est affiché avec la vraie carte produit du site (rendue par Shopify via
     templates/product.nuoma-fav.liquid), avec choix de la variante et « Ajouter au panier ».
     Prix, photos et disponibilités sont donc toujours ceux de la boutique. */
  const money = cents => {
    try { return new Intl.NumberFormat(document.documentElement.lang || 'fr-FR', {style: 'currency', currency: window.Shopify?.currency?.active || 'EUR'}).format(cents / 100); }
    catch (_) { return (cents / 100).toFixed(2) + ' €'; }
  };
  const esc = s => String(s ?? '').replace(/[&<>"']/g, c => ({'&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'}[c]));
  const sized = (src, w) => { if (!src) return ''; try { const u = new URL(src, location.href); u.searchParams.set('width', w); return u.toString(); } catch (_) { return src; } };
  const cards = new Map();

  function loadCard(handle) {
    if (!cards.has(handle)) {
      cards.set(handle, fetch(root() + 'products/' + encodeURIComponent(handle) + '?view=nuoma-fav', {headers: {Accept: 'text/html'}})
        .then(r => {
          if (r.status === 404) return {missing: true};
          if (!r.ok) return null;
          return r.text().then(html => {
            const node = new DOMParser().parseFromString(html, 'text/html').querySelector('[data-fav-card]');
            return node ? {node} : null;
          });
        })
        .catch(() => null));
    }
    return cards.get(handle);
  }

  // Carte de secours (si la boutique ne répond pas) : données enregistrées avec le favori.
  function fallbackCard(f) {
    const wrap = document.createElement('div');
    wrap.innerHTML = `
      <div class="nuoma-fav-item is-fallback" data-fav-card="${esc(f.handle)}">
        <article class="product-card">
          <div class="product-card__visual">
            <a class="product-card__media" href="${esc(f.url)}" aria-label="${esc(f.title)}">${f.image ? `<img class="product-card__img" src="${esc(sized(f.image, 700))}" alt="${esc(f.title)}" loading="lazy" width="700" height="760">` : ''}</a>
            <button type="button" class="product-card__fav is-active" data-fav-toggle data-handle="${esc(f.handle)}" data-title="${esc(f.title)}" data-url="${esc(f.url)}" data-image="${esc(f.image)}" data-price="${esc(f.price)}" aria-pressed="true" aria-label="Retirer ${esc(f.title)} des favoris"><svg class="nuoma-heart" viewBox="0 0 24 24" aria-hidden="true"><path d="M12 20.3s-7.6-4.6-9.2-9.4C1.7 7.6 3.9 4.5 7.1 4.5c2 0 3.6 1.1 4.9 2.9 1.3-1.8 2.9-2.9 4.9-2.9 3.2 0 5.4 3.1 4.3 6.4-1.6 4.8-9.2 9.4-9.2 9.4Z"/></svg></button>
          </div>
          <div class="product-card__info"><div><h3><a href="${esc(f.url)}">${esc(f.title)}</a></h3></div><div class="price"><span>${f.price ? money(f.price) : ''}</span></div></div>
        </article>
        <a class="btn btn--full nuoma-fav-buy__btn" href="${esc(f.url)}"><span class="nuoma-fav-buy__label">Voir la pièce</span></a>
      </div>`;
    return wrap.firstElementChild;
  }

  let renderToken = 0;
  function updateHead(page, n) {
    const count = page.querySelector('[data-favorites-total]');
    count.textContent = n ? `${n} pièce${n > 1 ? 's' : ''} sauvegardée${n > 1 ? 's' : ''}` : '';
    page.querySelector('[data-favorites-empty]').hidden = n > 0;
    page.querySelector('[data-favorites-grid]').hidden = n === 0;
  }

  async function renderPage() {
    const page = document.querySelector('[data-favorites-page]');
    if (!page) return;
    const grid = page.querySelector('[data-favorites-grid]');
    const list = memory.slice();
    const token = ++renderToken;
    updateHead(page, list.length);
    if (!list.length) { grid.replaceChildren(); return; }
    // Cartes déjà affichées conservées (pas de clignotement), nouvelles cartes en attente.
    const existing = new Map([...grid.querySelectorAll(':scope > [data-fav-card]')].map(c => [c.dataset.favCard, c]));
    const ordered = list.map(f => {
      const card = existing.get(f.handle);
      if (card) return card;
      const pending = fallbackCard(f);
      pending.classList.add('is-loading');
      return pending;
    });
    grid.replaceChildren(...ordered);
    const results = await Promise.all(list.map(f => loadCard(f.handle).then(r => [f, r])));
    if (token !== renderToken) return;
    const gone = [];
    results.forEach(([f, r]) => {
      const current = grid.querySelector(`:scope > [data-fav-card="${CSS.escape(f.handle)}"]`);
      if (!current) return;
      if (r?.missing) { gone.push(f.handle); current.remove(); return; }
      if (r?.node && current.classList.contains('is-loading')) current.replaceWith(document.importNode(r.node, true));
      else current.classList.remove('is-loading');
    });
    if (gone.length) write(memory.filter(f => !gone.includes(f.handle)));
    sync();
    updateHead(page, grid.querySelectorAll(':scope > [data-fav-card]').length);
  }

  // Cœur retiré depuis la page : la carte s'efface en douceur.
  document.addEventListener('nuoma:favorites:change', () => {
    const page = document.querySelector('[data-favorites-page]');
    if (!page) return;
    const grid = page.querySelector('[data-favorites-grid]');
    const handles = new Set(memory.map(f => f.handle));
    const shown = [...grid.querySelectorAll(':scope > [data-fav-card]')];
    if (memory.some(f => !shown.some(c => c.dataset.favCard === f.handle))) { renderPage(); return; }
    shown.filter(c => !handles.has(c.dataset.favCard)).forEach(card => {
      card.classList.add('is-removing');
      setTimeout(() => { card.remove(); updateHead(page, grid.querySelectorAll(':scope > [data-fav-card]').length); }, 320);
    });
    if (!handles.size) setTimeout(() => updateHead(page, 0), 330);
  });

  // Variante choisie : prix de la carte mis à jour ; pastille de couleur → même variante dans la liste.
  function showVariantPrice(item, option) {
    const price = item.querySelector('.product-card .price');
    if (!price || !option) return;
    const p = Number(option.dataset.price), c = Number(option.dataset.compare);
    price.innerHTML = (c > p ? `<s>${money(c)}</s>` : '') + `<span>${money(p)}</span>`;
  }
  document.addEventListener('change', event => {
    const select = event.target.closest('[data-fav-variant]');
    if (select) showVariantPrice(select.closest('.nuoma-fav-item'), select.selectedOptions[0]);
  });
  document.addEventListener('click', event => {
    const swatch = event.target.closest('.nuoma-fav-item .product-card__swatch');
    if (!swatch) return;
    const item = swatch.closest('.nuoma-fav-item');
    const select = item.querySelector('[data-fav-variant]');
    const id = (swatch.dataset.swatchUrl || '').match(/variant=(\d+)/)?.[1];
    const option = select && id ? [...select.options].find(o => o.value === id && !o.disabled) : null;
    if (option) { select.value = id; showVariantPrice(item, option); }
  });

  document.addEventListener('click', event => {
    const remove = event.target.closest('[data-fav-remove]');
    if (!remove) return;
    write(memory.filter(f => f.handle !== remove.dataset.favRemove));
  });

  function init() { sync(); renderPage(); }
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
  document.addEventListener('shopify:section:load', init);
  NUOMA.favorites = {list: () => memory.slice(), toggle};
})();

/* NUOMA 3.6.3 — « Suivre ma commande » : suivi par numéro d'expédition via le portail universel.
   Seul le numéro de colis est transmis au portail ; aucune donnée de commande n'est exposée. */
(() => {
  'use strict';
  document.addEventListener('submit', event => {
    const form = event.target.closest('[data-track-form]');
    if (!form) return;
    event.preventDefault();
    const input = form.querySelector('input[name="numero"]');
    const msg = form.querySelector('[data-track-msg]');
    const hint = msg.dataset.hint || (msg.dataset.hint = msg.textContent);
    const number = input.value.replace(/[\s.]+/g, '').toUpperCase();
    if (!/^[A-Z0-9-]{6,40}$/.test(number)) {
      form.classList.add('is-invalid');
      msg.textContent = number ? 'Ce numéro ne semble pas valide : il contient en général 8 à 30 lettres et chiffres, sans espace.' : 'Saisissez votre numéro d’expédition.';
      input.focus();
      return;
    }
    form.classList.remove('is-invalid');
    msg.textContent = hint;
    input.value = number;
    const url = (form.dataset.portal || 'https://t.17track.net/fr#nums={numero}').replace('{numero}', encodeURIComponent(number));
    const tab = window.open(url, '_blank');
    if (tab) tab.opener = null; // le portail n'a aucun accès à notre page
    else location.assign(url); // fenêtre bloquée : on ouvre le suivi ici
  });
  document.addEventListener('input', event => {
    const form = event.target.closest('[data-track-form].is-invalid');
    if (form) { form.classList.remove('is-invalid'); const msg = form.querySelector('[data-track-msg]'); if (msg.dataset.hint) msg.textContent = msg.dataset.hint; }
  });
})();
